<template>
  <div>
    <form v-if="artwork"
          class="artwork-editor"
          autocomplete="off"
          @submit.prevent="">
      <layout-push-sidebar ref="sidebar">
        <template v-slot:sidebar>
          <ul class="form-nav">
            <li class="form-nav-item" :class="{active:activeSectionId==='title'}">
              <label for="title" @click.stop.prevent="scrollToId('title')">
                <span>{{artwork.title || 'Title'}}</span>
              </label>
            </li>
            <li class="form-nav-item" :class="{active:activeSectionId==='config'}">
              <label @click.stop.prevent="scrollToId('config')">
                <span>Configurations</span>
              </label>
              <ul class="sub">
                <li class="form-nav-item" :class="{active:activeSectionId==='channels'}">
                  <label @click.stop.prevent="scrollToId('channels')">
                    <span>Channels (Players)</span>
                  </label>
                  <ul class="sub">
                    <li class="form-nav-item" :class="{active:activeSectionId==='source'}">
                      <label @click.stop.prevent="scrollToId('source')">Source</label>
                    </li>
                    <li class="form-nav-item" :class="{active:activeSectionId==='outputs'}">
                      <label @click.stop.prevent="scrollToId('outputs')">Outputs</label>
                    </li>
                    <li class="form-nav-item" :class="{active:activeSectionId==='preview'}">
                      <label @click.stop.prevent="scrollToId('preview')">Preview</label>
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
            <li class="form-nav-item" :class="{active:activeSectionId==='remote'}">
              <label @click.stop.prevent="scrollToId('remote')">
                <span>Remote Control</span>
              </label>
            </li>
            <li class="form-nav-item" :class="{active:activeSectionId==='credits'}">
              <label @click.stop.prevent="scrollToId('credits')">Credits</label>
            </li>
            <li class="form-nav-item" :class="{active:activeSectionId==='date'}">
              <label @click.stop.prevent="scrollToId('date')">Date of Creation</label>
            </li>
            <li class="form-nav-item" :class="{active:activeSectionId==='description'}">
              <label @click.stop.prevent="scrollToId('description')">Description</label>
            </li>
          </ul>
        </template>
        <template v-slot:content>
          <observable-scroll-box ref="editor"
                                 id="editor"
                                 observe-selector=".form-section"
                                 :observe-threshold="[0,0.25,0.5,0.75,1]"
                                 class="form"
                                 @in-view="selectInViewSection">
            <section class="title">
              <px-input id="title"
                        v-model.trim="artwork.title"
                        placeholder="Untitled Work"
                        no-label
                        class="big form-section" />
            </section>
            <div id="config" class="work-configs">
              <subject-with-help class="with-padding">
                <h3>Display Configurations</h3>
                <template v-slot:help>
                  <p>A Display Configuration is a description of all technical and aesthetic aspects of the work needed for it's future correct representation.</p>
                  <p>Each Work could have multiple display configurations.
                    For instance: a single monitor version and a large scale multi-projectors installation.</p>
                </template>
              </subject-with-help>
              <tabs ref="configTabs"
                    v-model="configTabs"
                    :editable="true"
                    plusButtonTitle="Add Work Configuration"
                    @add="addConfig"
                    @remove="removeConfig">
                <template v-slot:tab-content="{activeTabID}">
                  <keep-alive>
                    <template v-for="(config, i) in orderedConfigs">
                      <artwork-config-editor ref="configEditor"
                                             v-if="config.id === activeTabID"
                                             :key="config.id"
                                             :value="config"
                                             :single="orderedConfigs.length < 2"
                                             :index="i"
                                             @input="updateConfig"
                                             @remove="removeConfig"
                                             @newTitleCandidate="setTitleIfEmpty"/>
                    </template>
                  </keep-alive>
                </template>
              </tabs>
            </div>
            <section>
              <h3>Remote Control</h3>
              <div id="remote" class="row">
                <div class="field">
                  <remote-control-editor v-model="artwork.controls"/>
                </div>
              </div>
            </section>
            <section id="credits" class="form-section">
              <h4>Author</h4>
              <div class="field">
                <contributors-editor v-model="artwork.artists"/>
              </div>
              <h4>Credits</h4>
              <div class="field">
                <contributors-editor :withRoles="true" v-model="artwork.credits"/>
              </div>
              <h4>Year</h4>
              <div class="field">
                <input id="year" class="form-section" required="required" type="text" v-model.trim="artwork.year">
              </div>
            </section>
            <section>
              <h4>Description</h4>
              <div class="field">
                <textarea id="description" class="form-section" v-model.trim="artwork.description"/>
              </div>
            </section>
          </observable-scroll-box>
        </template>
      </layout-push-sidebar>
    </form>
    <footer class="editor-section">
      <router-link class="button frameless" to="/artworks" v-if="isNew">Cancel</router-link>
      <router-link :to="'/artwork/' + artworkId" class="button frameless" v-if="! isNew">Cancel</router-link>
      <button v-if="isNew" class="frameless" @click="submitArtwork">Create</button>
      <button v-else class="frameless" @click="submitArtwork">Save</button>
    </footer>
  </div>
</template>

<script>
import { objectToEntries } from '../../../lib/object-to-entries'
import { PxArtwork, PxAWConfiguration } from '@pxflux/px-notation'
import { mapActions, mapState } from 'vuex'
import firebase from '../../../firebase-app'
import LayoutPushSidebar from '../../elements/UI/layouts/LayoutWithPushSidebar'
import Tabs from '../../elements/UI/Tabs'
import ObservableScrollBox from '../../elements/UI/ObservableScrollBox'
import SubjectWithHelp from '../../elements/UI/SubjectWithHelp'
import PxInput from '../../elements/UI/inputs/PxInput'
import ArtworkConfigEditor from './ArtworkConfigEditor'
import RemoteControlEditor from './RemoteControlEditor'
import ContributorsEditor from './ContributorsEditor'

export default {
  name: 'ArtworkEditor',
  props: ['isNew'],
  components: {
    Tabs,
    RemoteControlEditor,
    ContributorsEditor,
    ArtworkConfigEditor,
    LayoutPushSidebar,
    SubjectWithHelp,
    ObservableScrollBox,
    PxInput
  },
  data () {
    return {
      artwork: PxArtwork.preset(),
      mainHeaderEl: null,
      activeSectionId: null,
      artworkOriginalSnapshot: ''
    }
  },
  computed: {
    ...mapState(['userAccount', 'accountArtwork']),

    accountId () {
      if (!this.userAccount) {
        return null
      }
      return this.userAccount['.key']
    },
    artworkId () {
      return this.$route.params.id
    },

    configTabs: {
      get () {
        return this.orderedConfigs.map((config, i) => ({
          id: config.id,
          label: config.title || (i ? 'Untitled' : 'Default')
        }))
      },
      set (val) {
        val.forEach((item, i) => {
          const index = this.artwork.configurations.findIndex(s => s.id === item.id)
          if (index >= 0) {
            this.$set(this.artwork.configurations[index], 'order', i)
          }
        })
      }
    },

    orderedConfigs () {
      return [...this.artwork.configurations].sort((a, b) => a.order - b.order)
    },

    configsIds () {
      return this.artwork.configurations.map(s => s.id)
    }
  },

  watch: {
    $route () {
      this.init()
    },
    userAccount () {
      this.init()
    },
    accountArtwork () {
      this.artwork = PxArtwork.from(this.accountArtwork)
      this.artworkOriginalSnapshot = JSON.stringify(this.artwork)
    },
    artwork (val) {
      // !!! DEBUG !!!
      console.log(`%c artwork UPDATED %c val: `, 'background:#00bbff;color:#000', 'color:#00aaff', val)
      const entries = objectToEntries(this.artwork, {}, 'artwork/')
      console.log(`%c artwork() %c entries: `, 'background:#00ff00;color:#000', 'color:#00aaff', entries)
    }
  },

  created () {
    this.init()
  },

  mounted () {
    this.mainHeaderEl = document.querySelector('header.main')
    this.artworkOriginalSnapshot = JSON.stringify(this.artwork)
  },

  methods: {
    ...mapActions(['setRef']),

    init () {
      if (!this.isNew && this.accountId) {
        const ref = firebase.database().ref('accounts/' + this.accountId + '/artworks/' + this.artworkId)
        this.setRef({ key: 'accountArtwork', ref })
      }
    },

    setTitleIfEmpty (title) {
      if (!this.artwork.title) {
        this.$set(this.artwork, 'title', title)
      }
    },

    updateConfig (config) {
      const index = this.getConfigIndexById(config.id)
      this.$set(this.artwork.configurations, index, config)
    },

    async addConfig () {
      const newConfig = PxAWConfiguration.preset()
      newConfig.order = this.artwork.configurations.length
      this.artwork.configurations.push(newConfig)
      await this.$nextTick()
      this.$refs.configTabs.switchTab(newConfig.id)
      await this.$nextTick()
      this.$refs.configEditor[0].focusName()
    },

    removeConfig (id) {
      const oc = this.orderedConfigs
      const index = oc.findIndex(c => c.id === id)
      const preId = (index && index - 1 >= 0) ? oc[index - 1].id : oc[0].id
      this.artwork.configurations.splice(index, 1)
      this.artwork.configurations.forEach((c, i) => { c.order = i })
      this.$refs.configTabs.switchTab(preId)
    },

    getConfigIndexById (id) {
      return this.artwork.configurations.findIndex(s => s.id === id)
    },

    submitArtwork () {
      if (!this.accountId) {
        return
      }
      const path = 'accounts/' + this.accountId + '/artworks/'
      const id = this.isNew ? firebase.database().ref(path).push().key : this.artworkId
      const updated = this.getUpdatedEntries(path + id + '/')
      // !!! DEBUG !!!
      console.log(`%c submitArtwork() %c updated: `, 'background:#ffbb00;color:#000', 'color:#00aaff', updated)
      // firebase.database().ref().update(updated).then(() => {
      //   this.$router.push('/artwork/' + id)
      // }).catch(log())
    },

    /**
     * @return {{}}
     */
    getUpdatedEntries (prefix) {
      const orig = PxArtwork.from(JSON.parse(this.artworkOriginalSnapshot))
      const original = objectToEntries(orig, {}, prefix)
      const current = objectToEntries(this.artwork, {}, prefix)
      return Object.entries(current).reduce((res, [key, value]) => {
        if (!original.hasOwnProperty(key) ||
          JSON.stringify(value) !== JSON.stringify(original[key])) {
        // !!! DEBUG !!!
          console.log(`%c () %c original[key] vs value`, 'background:#ffbb00;color:#000', 'color:#00aaff', original[key], value)
          res[key] = value
        }
        return res
      }, {})
    },

    scrollToId (targetId) {
      const headerHeight = parseInt(window.getComputedStyle(this.mainHeaderEl).height)
      this.$scrollTo(`#${targetId}`, 150, {
        container: '#editor',
        offset: -headerHeight * 2,
        easing: 'linear',
        force: true,
        y: true,
        x: false
      })
    },

    selectInViewSection (inViewList) {
      const active = inViewList[0]
      this.activeSectionId = active.el.id
    }
  }
}
</script>
<style lang="scss">
  @import "../../../scss/vars";
  $top-offset: $basic-size;
  .artwork-editor {
    height: 100vh;
    width: 100vw;

    .push-sidebar {
      padding-top: $top-offset;

      .form-nav {
        width: $basic-size * 4;
        li {
          padding: 0 0 0 $basic-padding;
          cursor: pointer;

          &.active {
            background: $px-color-green;
          }
        }

        .sub {
          text-align: end;
        }
        label {
          margin: 0;
          padding: 0;
          cursor: pointer;

          & > * {
            padding: 0;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          &.active {
            color: #000;
          }
        }
      }
    }

    .form {
      padding: $top-offset + $basic-size 0 $basic-padding;
      height: 100%;
      width: 100%;
      overflow: auto;
      scroll-behavior: smooth;

      & > section:last-child {
        min-height: 50vh;
      }
    }
  }
  .with-padding {
    padding: 0 $basic-padding;
  }
</style>
